import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'

import { useRouter } from 'next/router'

import styles from './style.module.scss'
import noImage from '@assets/images/placeholder/article-placeholder.jpg'
import { convertDt24hFormat, getImgURL, dateCheck, checkPageNumberInSlug } from '@shared-submodule/utils'
import { BLUR_DATA_URL_BASE64, DEFAULT_BLOG_READ } from '@shared-submodule/sm-constants'
import Layout from '../layout'
import { GET_ARTICLE_VIEWS, GET_FANTASY_ARTICLE_VIEWS } from '@graphql/article/article.query'
import { useLazyQuery } from '@apollo/client'
import MyImage from '@shared/components/myImage'
const ExclusiveIcon = dynamic(() => import('@shared/components/ctIcons/exclusiveIcon'), { ssr: false })
const EditorsPickIcon = dynamic(() => import('@shared/components/ctIcons/editorsPickIcon'), { ssr: false })

function ArticleDetail({ article, isPreviewMode, seoData, children, type, liveArticle }) {
  const router = useRouter()
  const [page, setPage] = useState(1)

  const [getArticleViews] = useLazyQuery(GET_ARTICLE_VIEWS)
  const [getFantasyArticleViews] = useLazyQuery(GET_FANTASY_ARTICLE_VIEWS)

  useEffect(() => {
    const slug = router?.asPath?.split('/').filter(e => e)
    const { lastSlug } = checkPageNumberInSlug(slug)
    setPage(lastSlug ? Number(lastSlug) || 1 : 1)
  }, [router?.asPath])

  useEffect(() => {
    if (article?.oSeo?.eType === 'ar') {
      getArticleViews({ variables: { input: { _id: article?._id } } })
    } else {
      getFantasyArticleViews({ variables: { input: { _id: article?._id } } })
    }
  }, [])

  return (
    <Layout data={{ ...article, oSeo: { ...article?.oSeo, ...seoData } }} isPreviewMode={isPreviewMode}>
      <main className={`${styles.articlePage}`}>
        <section className={`common-section ${type === 'fantasyArticle' ? 'pt-3' : 'pt-3 pt-md-4'}`}>
          <div className="container">
            <div className="row justify-content-center justify-content-lg-start">
              <div>
                <section className={`${styles.articleBlock} pb-2 mb-2 pt-0 position-relative`}>
                  <h1 className={`mb-0 ${styles.hOveride}`}>{article?.sTitle}</h1>
                  <div
                    className={`d-flex align-items-start justify-content-between ${
                      article?.oAdvanceFeature?.bEditorsPick && article?.oAdvanceFeature?.bExclusiveArticle && 'flex-column flex-sm-row'
                    }`}>
                    <div className="d-flex mb-2 mb-md-0">
                      {article?.oAdvanceFeature?.bEditorsPick && (
                        <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize`}>
                          <EditorsPickIcon />
                          {"Editor's Pick"}
                        </span>
                      )}
                      {article?.oAdvanceFeature?.bExclusiveArticle && (
                        <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize ms-2`}>
                          <ExclusiveIcon />
                          {'Exclusive Article'}
                        </span>
                      )}
                    </div>
                    {article?._id === '63a40babfdaa420306916fd6' && <p className={`text-danger ${styles.liveUpdate}`}>LIVE</p>}
                  </div>
                  <article>
                    <div className={`${styles.authorBlock} d-flex flex-wrap align-items-center mb-3 mb-lg-4 mt-1`}>
                      {article?.iEventId ? (
                        <span className={`${styles.liveblog} text-uppercase d-none d-sm-block position-relative me-4 `}>{'Live Blog'}</span>
                      ) : null}
                      <div className={`${styles.dates} w-100 xsmall-text font-semi mt-2 mb-0 d-flex`}>
                        <div>
                          {(article?.dPublishDisplayDate || article?.dPublishDate) && (
                            <>
                              <time
                                className="op-published"
                                dateTime={dateCheck(article?.dPublishDisplayDate || article?.dPublishDate)?.toISOString()}>
                                {/* {"Published"} -{' '} */}
                                {convertDt24hFormat(dateCheck(article?.dPublishDisplayDate || article?.dPublishDate))}
                              </time>
                            </>
                          )}
                          &nbsp;by <span className={styles.publisher}>
                            {article?.oDisplayAuthor?.oSeo?.sSlug ? article?.oDisplayAuthor?.sDisplayName : article?.oDisplayAuthor?.sDisplayName}
                          </span>
                        </div>

                        <div>
                          <p className={'d-flex align-items-center mb-0'}>
                            {article?.nDuration > 0 ? article?.nDuration : DEFAULT_BLOG_READ} min read
                          </p>
                        </div>
                      </div>
                      {article?.iEventId ? (
                        <span className={`${styles.liveblog} text-uppercase d-sm-none position-relative me-4 `}>{'Live Blog'}</span>
                      ) : null}
                    </div>
                    {page <= 1 && (
                      <figure className="op-tracker">
                        <div className={`${styles.postImg} position-relative overflow-hidden`}>
                          <MyImage
                            priority={true}
                            src={getImgURL(article?.oImg?.sUrl) || noImage}
                            alt={article?.oImg?.sText || article?.sSrtTitle}
                            placeholder="blur"
                            blurDataURL={BLUR_DATA_URL_BASE64}
                            height="160"
                            width="256"
                            layout="responsive"
                            sizes="(max-width: 767px) 320px, (max-width: 991px) 360px, (max-width: 1190px) 400px, 600px"
                          />
                        </div>
                      </figure>
                    )}
                    {children}
                  </article>
                </section>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

ArticleDetail.propTypes = {
  article: PropTypes.object,
  liveArticle: PropTypes.object,
  seoData: PropTypes.object,
  isPreviewMode: PropTypes.bool,
  children: PropTypes.node.isRequired,
  type: PropTypes.string
  // currentSeriesData: PropTypes.object
}

export default ArticleDetail
